import { ThemeEvents, CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Keeps the VitaEssence VIP free-gift + shipping-protection line items in
 * sync with the real cart, from a script that is guaranteed to execute on
 * every real page load (loaded globally from snippets/header-actions.liquid).
 *
 * This deliberately does NOT live in snippets/cart-summary.liquid: that
 * section's own <script> tags only run the first time its markup arrives
 * via a full page load. The cart drawer's first-ever open populates that
 * same markup by injecting fetched HTML into the DOM, and browsers never
 * execute a <script> that arrives that way — so on a customer's very first
 * add-to-cart, functions defined there (and the checkbox's inline
 * onchange handler that calls them) silently don't exist yet. Only after
 * a real navigation (e.g. a refresh) does that script tag get a chance to
 * run. Living here instead sidesteps the problem entirely, using event
 * delegation on `document` (which is never replaced by any section morph)
 * for both the automatic reconciliation and the manual toggle click.
 *
 * Neklamp bundle variants, expressed as "lamps per unit":
 *   1 stuk  (57456618602878) -> 1 lamp
 *   2-pack  (57921272938878) -> 2 lamps
 *   3-pack  (57921272971646) -> 3 lamps
 *
 * Free gifts (price €0), thresholds match sections/bestellen-landing-vip.liquid:
 *   Kunstzinnige Bladwijzers (58011660452222) from 2+ lamps
 *   Handgemaakt Cadeautje    (58011660747134) from 3+ lamps
 *
 * Shipping protection (Pakketbescherming, 57971765117310, paid add-on):
 *   suggested from 2+ lamps, but only ever auto-ADDED if the customer
 *   hasn't explicitly declined it before (vitaessence_protection_declined
 *   in localStorage) — auto-REMOVE always applies regardless of that flag,
 *   since dropping below 2 lamps means the fee no longer applies at all.
 */
const LAMP_VARIANTS = {
  57456618602878: 1,
  57921272938878: 2,
  57921272971646: 3,
};

const GIFTS = [
  { variantId: 58011660747134, minLamps: 3 },
  { variantId: 58011660452222, minLamps: 2 },
];

const PROTECTION_VARIANT_ID = 57971765117310;
const PROTECTION_MIN_LAMPS = 2;
const PROTECTION_DECLINED_KEY = 'vitaessence_protection_declined';
// TIJDELIJK UIT (conversietest vanaf 2026-09-28): zet op true om pakketbescherming
// terug aan te zetten. Zolang dit false is, wordt ze nooit toegevoegd en wordt ze
// uit elk mandje verwijderd (ook niet-VIP), omdat de regel in het mandje verborgen is.
const PROTECTION_ENABLED = false;

const ONLY_VIP_LINES = false;
const SYNC_SOURCE = 've-gift-sync';
let syncing = false;

function countVipLamps(cart) {
  // Sinds 2026-09-29 is de cadeau-actie de standaard voor iedereen (de aparte
  // VIP-pagina is opgeheven), dus elke Neklamp in het mandje telt mee. Wil je
  // de cadeaus later weer beperken tot de VIP-knoppen, zet dan ONLY_VIP_LINES op true:
  // alleen regels met de property _ve_vip tellen dan nog mee.
  return cart.items.reduce((total, item) => {
    if (ONLY_VIP_LINES && !(item.properties && item.properties._ve_vip === 'true')) return total;
    const perUnit = LAMP_VARIANTS[item.variant_id];
    return perUnit ? total + perUnit * item.quantity : total;
  }, 0);
}

function currentQuantity(cart, variantId) {
  const line = cart.items.find((item) => item.variant_id === variantId);
  return line ? line.quantity : 0;
}

function protectionDeclined() {
  try {
    return localStorage.getItem(PROTECTION_DECLINED_KEY) === '1';
  } catch (e) {
    return false;
  }
}

async function reconcileCart(cart) {
  if (syncing || !cart || !Array.isArray(cart.items)) return;

  const lamps = countVipLamps(cart);
  // No early bail-out on lamps === 0: that used to also skip cleanup when
  // the customer deletes the Neklamp line entirely (e.g. the bin icon in
  // the cart), leaving the free gifts/protection stranded behind. It's
  // still safe for a completely unrelated cart — every branch below is a
  // no-op unless a gift/protection line is already present to remove, and
  // a cart with 0 VIP lamps never qualifies to ADD one (see qualifiesForProtection
  // and the GIFTS loop, both gated on `lamps`).

  const updates = {};

  for (const gift of GIFTS) {
    const shouldHave = lamps >= gift.minLamps ? 1 : 0;
    if (currentQuantity(cart, gift.variantId) !== shouldHave) {
      updates[gift.variantId] = shouldHave;
    }
  }

  const hasProtection = currentQuantity(cart, PROTECTION_VARIANT_ID) > 0;
  const qualifiesForProtection = lamps >= PROTECTION_MIN_LAMPS;
  if (!PROTECTION_ENABLED) {
    if (hasProtection) updates[PROTECTION_VARIANT_ID] = 0;
  } else if (hasProtection && !qualifiesForProtection) {
    updates[PROTECTION_VARIANT_ID] = 0;
  } else if (!hasProtection && qualifiesForProtection && !protectionDeclined()) {
    updates[PROTECTION_VARIANT_ID] = 1;
  }

  if (Object.keys(updates).length === 0) return;

  syncing = true;
  try {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
    const updatedCart = await response.json();

    // Let cart-drawer / cart-items re-render with the corrected lines.
    document.dispatchEvent(
      new CartAddEvent(updatedCart, SYNC_SOURCE, {
        source: SYNC_SOURCE,
        itemCount: updatedCart.item_count,
      })
    );
  } catch (error) {
    console.error('[ve-gift-sync] failed to sync VIP gifts/protection', error);
  } finally {
    syncing = false;
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.source === SYNC_SOURCE) return;
  reconcileCart(event.detail?.resource);
});

// Cover landing straight on /cart, or opening the drawer, before any cart:update has fired.
fetch('/cart.js')
  .then((response) => response.json())
  .then(reconcileCart)
  .catch(() => {});

// Manual toggle in the cart summary (snippets/cart-summary.liquid renders the
// checkbox itself but intentionally no longer wires up its own handler — see
// the file header comment above for why that broke on a customer's first
// add-to-cart). Delegation on `document` means this works regardless of
// when/how that checkbox's markup enters the DOM.
document.addEventListener('change', (event) => {
  const input = event.target.closest?.('.cart-protection__toggle input[data-variant-id]');
  if (!input) return;

  const sectionId = input.dataset.sectionId;
  const variantId = input.dataset.variantId;
  if (!sectionId || !variantId) return;

  const checked = input.checked;
  input.disabled = true;

  (async () => {
    try {
      try {
        if (checked) {
          localStorage.removeItem(PROTECTION_DECLINED_KEY);
        } else {
          localStorage.setItem(PROTECTION_DECLINED_KEY, '1');
        }
      } catch (e) {}

      const url = checked ? '/cart/add.js' : '/cart/update.js';
      const body = checked ? { id: variantId, quantity: 1 } : { updates: { [variantId]: 0 } };

      const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
      if (!response.ok) throw new Error('cart request failed');

      await sectionRenderer.renderSection(sectionId, { cache: false });
    } catch (error) {
      window.location.reload();
    }
  })();
});
